"use client";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { Cinzel_Decorative } from "next/font/google";
import { useForm, FormProvider } from "react-hook-form";
import Link from "next/link";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import bgc from "../../../public/background_card_2.png";
import { UserService } from "@/services/user.service";
import { RegisterSchema, registerSchema } from "@/validators";
import { StorageHelper } from "@/helpers/StorageHelper";
import { useAuth } from "@/context/AuthContext";

const cinzel = Cinzel_Decorative({
  weight: ["400", "700", "900"],
  preload: false,
});

export default function Register() {
  const router = useRouter();
  const { createUser, login, getUser } = UserService;
  const { setItem } = StorageHelper;
  const { setUser } = useAuth();

  const methods = useForm<RegisterSchema>({
    resolver: zodResolver(registerSchema),
  });
  const {
    handleSubmit,
    formState: { errors },
  } = methods;

  const onSubmit = async (data: RegisterSchema) => {
    try {
      const payload = { name: data.name, password: data.password };
      const user = await createUser({ ...payload, email: data.email });
      if (!user) throw Error();
      toast.success("User created successfully");
      const logged = await login(data);
      if (!logged) throw Error();
      setItem("userData", logged);
      const userGet = await getUser(logged?.user.id);
      setUser(userGet!);
      router.push("/dashboard");
    } catch (e) {
      console.log(e);
      toast.error("An unexpected error occurred. Please try again later.");
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        backgroundImage: `url(${bgc.src})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <Header />

      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="relative w-full max-w-md p-6 bg-zinc-900 rounded-md shadow-md">
          <div className={cinzel.className}>
            <h5 className="text-center font-bold text-sm text-yellow-500">
              Registration for the new user
            </h5>
          </div>

          <div className="relative mt-4">
            <Image
              src={bgc}
              alt="Background"
              layout="fill"
              objectFit="cover"
              className="absolute inset-0 z-0 opacity-10"
            />

            <FormProvider {...methods}>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="relative z-10 space-y-4"
              >
                <FormField
                  control={methods.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white">Account Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Your username"
                          {...field}
                          className="bg-zinc-500 text-zinc-900"
                        />
                      </FormControl>
                      {errors.name && (
                        <FormMessage className="text-white">
                          {errors.name.message}
                        </FormMessage>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={methods.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white">Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="Your principal email"
                          {...field}
                          className="bg-zinc-500 text-zinc-900"
                        />
                      </FormControl>
                      {errors.email && (
                        <FormMessage className="text-white">
                          {errors.email.message}
                        </FormMessage>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={methods.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white">Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="••••••••"
                          {...field}
                          className="bg-zinc-500 text-zinc-900"
                        />
                      </FormControl>
                      {errors.password && (
                        <FormMessage className="text-white">
                          {errors.password.message}
                        </FormMessage>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={methods.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white">
                        Confirm Password
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="••••••••"
                          {...field}
                          className="bg-zinc-500 text-zinc-900"
                        />
                      </FormControl>
                      {errors.confirmPassword && (
                        <FormMessage className="text-white">
                          {errors.confirmPassword.message}
                        </FormMessage>
                      )}
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full mt-4 bg-orange-500 hover:bg-orange-600/90 text-white px-4 py-2 rounded-md"
                >
                  Register
                </Button>
                <div>
                  <Link href="/login" className="text-white">
                    Do you already have an account? Log in
                  </Link>
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
